// Home — the single-page dashboard. The group/category picker lives in the
// topbar (AppShell); this page owns ONE global period selection —
// Day/Week/Month/Year + prev/next stepper, or a free Custom date range —
// that drives BOTH the meter-card
// values (consumption inside the selected window) and the selected meter's
// detail panel below, like the legacy portal's month strip did. The first
// meter is auto-selected; the selected card stays bright while the others
// dim. Group + meter selection ride in the URL (?g=<id><type>&m=<mid>) so
// the view is bookmarkable.

import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motionSafeBehavior } from '../lib/a11y';
import { onAlertAcknowledged, withAcknowledged } from '../lib/alertEvents';
import { getMyGroupMeters, isAdmin, type MeterCard } from '../lib/api';
import { useEdgeFade } from '../lib/edgeFade';
import { groupKey, useGroupName, useSelectedGroup } from '../lib/group';
import { wordingFor } from '../lib/projectType';
import {
  customRangeEnd,
  customRangeTitle,
  endOfPeriod,
  meterTypeRank,
  periodTitle,
  seedCustomRange,
  startOfPeriod,
  stepPeriod,
  VIEW_MODE_KEYS,
  viewModeLabel,
  type CustomRange,
  type ViewMode,
} from '../lib/meterCharts';
import { useUser } from '../lib/user';
import { fromWall, LEGACY_ZONE, wallNow } from '../lib/zone';
import GroupShareModal from '../components/GroupShareModal';
import Icon from '../components/Icon';
import MeterPanel from '../components/MeterPanel';
import MeterTile from '../components/MeterTile';
import PeriodPicker from '../components/PeriodPicker';
import RangePicker from '../components/RangePicker';
import { Segmented, Spinner, StateCard } from '../components/ui';

export default function HomePage(): ReactElement {
  const { t } = useTranslation();
  const { me, loading: meLoading, error: meError, reload } = useUser();
  const { groups, group } = useSelectedGroup();
  const groupName = useGroupName();
  const [params, setParams] = useSearchParams();
  // Periods are the group's project days (lib/zone.ts): every period below
  // is a wall date on that calendar, converted only for the API.
  const tz = group?.project_timezone || LEGACY_ZONE;

  // Global period: view mode + anchor date, shared by the cards and the
  // panel. Always opens on the current Month (like the legacy portal).
  // 'custom' is the fifth mode — a free [from, to] day range instead of a
  // calendar-aligned period around the anchor.
  const [view, setView] = useState<ViewMode | 'custom'>('month');
  const [anchor, setAnchor] = useState<Date>(() => wallNow(tz));
  const [range, setRange] = useState<CustomRange>(() =>
    seedCustomRange(wallNow(tz), 'month', null, null, wallNow(tz)),
  );

  // The user's occupancy window on this group: calculation_date = move-in,
  // moved_out_date = move-out (both 'YYYY-MM-DD', UTC-midnight like the
  // period anchors). The stepper must not offer periods entirely outside it.
  const minDate = group?.calculation_date ? new Date(group.calculation_date) : null;
  const maxDate = group?.moved_out_date ? new Date(group.moved_out_date) : null;

  // "Now", pulled into the window — for moved-out groups land on the last
  // period the user may see instead of an empty current one.
  const clampedNow = () => {
    const now = wallNow(tz);
    let a = now;
    if (maxDate && maxDate < a) a = maxDate;
    if (minDate && a < minDate) a = minDate;
    return a;
  };

  // Re-anchor when the group changes — each group has its own window. The
  // custom range is pulled into the new window too (same object back when
  // nothing changed, so the fetch memo doesn't re-fire needlessly).
  useEffect(() => {
    setAnchor(clampedNow());
    setRange((r) => {
      const hi = startOfPeriod(clampedNow(), 'day');
      const to = r.to > hi ? hi : r.to;
      let from = minDate && r.from < minDate ? minDate : r.from;
      if (from > to) from = to;
      return from === r.from && to === r.to ? r : { from, to };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- min/max derive from group
  }, [group]);

  const [meters, setMeters] = useState<MeterCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0); // retry trigger
  const panelRef = useRef<HTMLDivElement>(null);
  const gridRef = useEdgeFade<HTMLDivElement>();
  const scrollOnSelect = useRef(false);
  const [shareOpen, setShareOpen] = useState(false);

  // The selected window. `to` is clamped to "now" for the running period —
  // same semantics the mobile home ranges had. MEMOIZED on the period
  // selection: `new Date()` on the running period changes every render, and
  // with `to` in the fetch effect's deps an unmemoized value re-triggers the
  // fetch after each response — an infinite request loop.
  const { periodStart, from, to } = useMemo(() => {
    const now = wallNow(tz);
    if (view === 'custom') {
      const end = customRangeEnd(range);
      return {
        periodStart: range.from,
        from: fromWall(range.from, tz).toISOString(),
        to: fromWall(end > now ? now : end, tz).toISOString(),
      };
    }
    const start = startOfPeriod(anchor, view);
    const end = endOfPeriod(start, view);
    return {
      periodStart: start,
      from: fromWall(start, tz).toISOString(),
      to: fromWall(end > now ? now : end, tz).toISOString(),
    };
  }, [anchor, view, range, tz]);

  useEffect(() => {
    if (!group) return;
    let alive = true;
    setMeters(null);
    setError(null);
    getMyGroupMeters(group.type, group.id, { from, to, tz })
      .then((list) => {
        if (alive) setMeters(list);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [group, from, to, tz, nonce]);

  // An alert acknowledged in the selected meter's alerts modal: swap it into
  // every card of that device, so its badge clears without a reload.
  useEffect(
    () =>
      onAlertAcknowledged(({ deveui, alert }) => {
        const eui = deveui.toLowerCase();
        setMeters((prev) => {
          if (!prev) return prev;
          let changed = false;
          const next = prev.map((m) => {
            if ((m.deveui ?? '').toLowerCase() !== eui) return m;
            const alerts = withAcknowledged(m.alerts, alert);
            if (alerts === m.alerts) return m;
            changed = true;
            return { ...m, alerts };
          });
          return changed ? next : prev;
        });
      }),
    [],
  );

  const changeView = (v: ViewMode | 'custom') => {
    if (v === 'custom') {
      // Seed the range from the period currently on screen so the switch
      // keeps showing the same window.
      if (view !== 'custom') setRange(seedCustomRange(anchor, view, minDate, maxDate, wallNow(tz)));
      setView('custom');
      return;
    }
    setView(v);
    setAnchor(clampedNow());
  };

  // The previous period [prevStart, periodStart) overlaps the window iff it
  // ends after the move-in date; the next period must not start after now or
  // the move-out date. Custom has no stepper — the range picker replaces it.
  const canPrev = !minDate || periodStart > minDate;
  const nextStart = view === 'custom' ? null : stepPeriod(periodStart, view, 1);
  const canNext =
    nextStart !== null && nextStart <= wallNow(tz) && (!maxDate || nextStart <= maxDate);

  const sorted = useMemo(
    () =>
      meters
        ? [...meters].sort(
            (a, b) =>
              meterTypeRank(a.icon) - meterTypeRank(b.icon) || a.name.localeCompare(b.name),
          )
        : null,
    [meters],
  );

  // Inline meter selection (?m=<mid>). The legacy portal defaulted to the
  // first meter so the chart is visible without any click — same here.
  const selectedMid = params.get('m');
  const selected = sorted?.find((m) => m.mid === selectedMid) ?? null;

  useEffect(() => {
    if (!group || !sorted || sorted.length === 0) return;
    if (selected) return;
    setParams({ g: groupKey(group), m: sorted[0].mid }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the list or selection changes
  }, [sorted, selected, group]);

  const selectMeter = (mid: string) => {
    if (!group) return;
    scrollOnSelect.current = true;
    setParams({ g: groupKey(group), m: mid }, { replace: true });
  };

  // Bring the panel into view on a manual card click (not on auto-select).
  useEffect(() => {
    if (!scrollOnSelect.current || !selected) return;
    scrollOnSelect.current = false;
    panelRef.current?.scrollIntoView({ behavior: motionSafeBehavior(), block: 'start' });
  }, [selected]);

  // Keep the selected card visible in the horizontal strip (the legacy
  // portal's ScrollToActiveMeter) — matters when a bookmark restores a meter
  // that sits beyond the fold. block:'nearest' avoids vertical page jumps.
  // Skipped when the card is already in view: even a no-op scrollIntoView
  // moves the browser's sequential-focus starting point, so the first Tab
  // would land mid-strip instead of on the skip link.
  useEffect(() => {
    if (!selected) return;
    const el = document.querySelector('.metric-card.selected');
    const strip = el?.parentElement;
    if (!el || !strip) return;
    const card = el.getBoundingClientRect();
    const view = strip.getBoundingClientRect();
    if (card.left >= view.left && card.right <= view.right) return;
    el.scrollIntoView({ behavior: motionSafeBehavior(), block: 'nearest', inline: 'nearest' });
  }, [selected]);

  if (meLoading) return <Spinner />;
  if (meError) {
    return (
      <StateCard
        title={t('common.error')}
        body={meError}
        action={
          <button className="btn" onClick={reload}>
            <Icon name="refresh" size={16} /> {t('common.retry')}
          </button>
        }
      />
    );
  }

  if (groups.length === 0) {
    return <StateCard icon="home" title={t('home.noGroupsTitle')} body={t('home.noGroupsBody')} />;
  }

  const rangeLabel = view === 'custom' ? customRangeTitle(range) : periodTitle(anchor, view);
  const project = me?.projects.find((p) => p.pid === group?.project_pid);

  return (
    <>
      <div className="home-toolbar">
        <Segmented
          options={[
            ...VIEW_MODE_KEYS.map((v) => ({ value: v as ViewMode | 'custom', label: viewModeLabel(v) })),
            { value: 'custom', label: t('common.custom') },
          ]}
          value={view}
          onChange={changeView}
        />
        <div className="spacer" />
        <div className="period-nav">
          {view === 'custom' ? (
            <RangePicker
              range={range}
              min={minDate}
              max={maxDate}
              now={wallNow(tz)}
              onChange={setRange}
            />
          ) : (
            <>
              <button
                className="icon-btn"
                disabled={!canPrev}
                onClick={() => setAnchor(stepPeriod(periodStart, view, -1))}
                data-tip={t('ui.previousPeriod')}
                aria-label={t('ui.previousPeriod')}
              >
                <Icon name="chevron-left" size={20} />
              </button>
              <PeriodPicker
                view={view}
                anchor={anchor}
                min={minDate}
                max={maxDate}
                now={wallNow(tz)}
                onChange={setAnchor}
              />
              <button
                className="icon-btn"
                disabled={!canNext}
                onClick={() => nextStart && setAnchor(nextStart)}
                data-tip={t('ui.nextPeriod')}
                aria-label={t('ui.nextPeriod')}
              >
                <Icon name="chevron-right" size={20} />
              </button>
            </>
          )}
        </div>
        {group && isAdmin(group.role) && (
          <button
            className="icon-btn"
            onClick={() => setShareOpen(true)}
            data-tip={t('share.button')}
            aria-label={t('share.button')}
          >
            <Icon name="share" size={18} />
          </button>
        )}
      </div>

      {shareOpen && group && (
        <GroupShareModal group={group} onClose={() => setShareOpen(false)} />
      )}

      {error && (
        <StateCard
          title={t('common.error')}
          body={error}
          action={
            <button className="btn" onClick={() => setNonce((n) => n + 1)}>
              <Icon name="refresh" size={16} /> {t('common.retry')}
            </button>
          }
        />
      )}

      {!error && !sorted && <Spinner />}

      {sorted && sorted.length === 0 && (
        <StateCard
          icon="speedometer"
          title={t(group?.type === 'c' ? 'home.noMetersCategory' : 'home.noMetersGroup', {
            context: wordingFor(group?.project_type),
          })}
          body={project?.no_meters_message || undefined}
        />
      )}

      {sorted && sorted.length > 0 && group && (
        <>
          <div ref={gridRef} className={`meter-grid${selected ? ' has-selection' : ''}`}>
            {sorted.map((m) => (
              <MeterTile
                key={m.mid}
                meter={m}
                selected={m.mid === selected?.mid}
                onSelect={() => selectMeter(m.mid)}
                rangeLabel={rangeLabel}
              />
            ))}
          </div>

          {selected && (
            <div ref={panelRef} className="inline-panel">
              <MeterPanel
                key={`${groupKey(group)}-${selected.mid}`}
                mid={selected.mid}
                pid={group.project_pid}
                group={{ type: group.type, id: group.id }}
                gname={groupName(group)}
                pname={group.project_name}
                period={view === 'custom' ? { view, range } : { view, anchor }}
                tz={tz}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}
